import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Live round timer pill under the scoreboard (admin match page + public match page).
 * Driven by `match_timer_update` WS messages from server/services/matchTimerService.js.
 */

// Seconds → M:SS
const formatTime = (seconds) => {
  if (seconds == null || seconds < 0) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const COLORS = {
  green: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.2)', text: '#22c55e' },
  red: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.2)', text: '#ef4444' },
  gray: { bg: 'rgba(156,163,175,0.1)', border: 'rgba(156,163,175,0.2)', text: '#9ca3af' },
  blue: { bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.2)', text: '#3b82f6' },
  amber: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', text: '#f59e0b' },
};

function MatchTimerPill({ match, matchTimer, style }) {
  const { t } = useTranslation();
  if (!match || match.status !== 'live') return null;

  const stage = match.match_stage || match.status;
  let text = '';
  let color = COLORS.green;

  if (stage === 'warmup') {
    text = t('matchDetail.timerWarmup', 'WARMUP');
    color = COLORS.amber;
  } else if (stage === 'knife') {
    text = t('matchDetail.timerKnife', 'KNIFE ROUND');
    color = COLORS.blue;
  } else if (stage === 'live' && matchTimer) {
    switch (matchTimer.type) {
      case 'freezetime':
        text = t('matchDetail.timerFreezetime', 'Freezetime');
        break;
      case 'round':
        text = formatTime(matchTimer.remaining);
        break;
      case 'bomb_planted':
        text = t('matchDetail.timerC4', 'C4: {{time}}', { time: formatTime(matchTimer.remaining) });
        color = COLORS.red;
        break;
      case 'round_end':
        text = t('matchDetail.timerRestart', 'Restart: {{time}}', { time: formatTime(matchTimer.remaining) });
        color = COLORS.gray;
        break;
      case 'halftime':
        text = t('matchDetail.timerHalftime', 'Halftime: {{time}}', { time: formatTime(matchTimer.remaining) });
        color = COLORS.blue;
        break;
      case 'timeout':
        text = t('matchDetail.timerTeamTimeout', '{{team}} Timeout', { team: matchTimer.team_name || t('matchDetail.timerTeam', 'Team') });
        color = COLORS.amber;
        break;
      case 'paused':
        text = t('matchDetail.timerPaused', 'Match Paused');
        color = COLORS.red;
        break;
      default:
        break;
    }
  }

  if (!text) return null;

  const isCountdown = stage === 'live';
  return (
    <div style={{
      marginTop: '0.5rem',
      padding: '4px 12px',
      borderRadius: 'var(--radius-sm, 6px)',
      background: color.bg,
      border: `1px solid ${color.border}`,
      fontFamily: 'var(--font-mono)',
      fontSize: '0.75rem',
      fontWeight: 700,
      color: color.text,
      letterSpacing: '0.04em',
      ...(isCountdown ? { minWidth: '80px', textAlign: 'center' } : {}),
      ...style,
    }}>
      {text}
    </div>
  );
}

export default MatchTimerPill;
